/** <product-sticky-cta>: mobile (<=750px) sticky buy bar of the PDP — inline sizes checking the shared form's radios, and the add button being a real submit of that form. */

import { Component } from "@theme/component";

class ProductStickyCta extends Component {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {IntersectionObserver | undefined} */
  #footerObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#form = document.querySelector("product-form-component form");

    this.addEventListener("click", this.#onClick);
    this.#form?.addEventListener("change", this.#onVariantChange);

    this.#syncQtyButtons();
    this.#observeFooter();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#form?.removeEventListener("change", this.#onVariantChange);
    this.#footerObserver?.disconnect();
  }

  #onClick = (event) => {
    // Boutons +/- de la quantité (produit à variante unique).
    const step = event.target.closest("[data-qty-step]");
    if (step && this.contains(step)) {
      this.#stepQty(Number(step.dataset.qtyStep));
      return;
    }

    const size = event.target.closest("[data-sticky-size]");
    if (size && this.contains(size)) this.#selectVariant(size.dataset.stickySize);
  };

  /**
   * Coche le radio de variante correspondant et émet un `change` qui bulle, comme le ferait un clic natif sur un <label for>.
   * @param {string} variantId
   */
  #selectVariant(variantId) {
    const radio = this.#form?.querySelector(`input[name="id"][value="${variantId}"]`);
    if (!radio || radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Changement de variante (depuis la barre OU la colonne info) : resync la taille active.
  #onVariantChange = (event) => {
    if (!event.target || event.target.name !== "id") return;

    const checked = this.#form?.querySelector('input[name="id"]:checked');
    if (!checked) return;

    for (const size of this.querySelectorAll("[data-sticky-size]")) {
      const active = size.dataset.stickySize === checked.value;
      size.classList.toggle("is-active", active);
      size.setAttribute("aria-pressed", String(active));
    }
  };

  // Incrémente/décrémente la quantité, bornée à `min` et au stock disponible (`max`).
  #stepQty(delta) {
    const input = this.querySelector("[data-qty-input]");
    if (!input) return;
    const min = Number.parseInt(input.min, 10) || 1;
    const max = Number.parseInt(input.max, 10);
    const current = Number.parseInt(input.value, 10) || min;
    let next = Math.max(min, current + delta);
    // `max` absent (stock non suivi ou survente autorisée) → pas de plafond.
    if (Number.isFinite(max)) next = Math.min(max, next);
    input.value = next;
    this.#syncQtyButtons();
  }

  // Désactive « - » à la quantité minimale et « + » au stock disponible.
  #syncQtyButtons() {
    const input = this.querySelector("[data-qty-input]");
    if (!input) return;
    const minus = this.querySelector('[data-qty-step="-1"]');
    const plus = this.querySelector('[data-qty-step="1"]');
    const min = Number.parseInt(input.min, 10) || 1;
    const max = Number.parseInt(input.max, 10);
    const current = Number.parseInt(input.value, 10) || min;
    if (minus) minus.disabled = current <= min;
    if (plus) plus.disabled = Number.isFinite(max) && current >= max;
  }

  // Masque la barre quand le footer entre dans le viewport (comme l'ancien thème).
  #observeFooter() {
    const footer = document.querySelector("footer");
    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.classList.toggle("is-hidden", entry.isIntersecting);
      },
      { threshold: 0 },
    );
    this.#footerObserver.observe(footer);
  }
}

if (!customElements.get("product-sticky-cta")) {
  customElements.define("product-sticky-cta", ProductStickyCta);
}
